import { Box, VStack, HStack, Text, Button, Badge, IconButton, Tabs, TabList, TabPanels, Tab, TabPanel, Avatar, useColorModeValue, useBreakpointValue, useToast, Spinner, Center, Progress, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure } from '@chakra-ui/react';
import { FiArrowLeft, FiThumbsUp, FiThumbsDown, FiShare, FiHeart, FiBook, FiCalendar, FiGlobe, FiStar } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import BookReader from '../components/BookReader';
import CommentSection from '../components/CommentSection';
import { useAuth } from '../contexts/AuthContext';
import { addBookToLibrary, removeBookFromLibrary, isBookInLibrary } from '../services/bookLibraryService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { deleteContent } from '../services/contentDeletionService';

interface ComicData {
  id: string;
  title: string;
  author: string;
  description: string;
  thumbnail: string;
  rating: number;
  year: number;
  genres: string[];
  language: string;
  uploader: string;
  uploaderId: string;
  uploaderAvatar?: string;
  views: number;
  likes: number;
  dislikes: number;
  tags: string[];
  category: string;
  assets?: {
    bookFile?: string;
    coverFile?: string;
    volumes?: Array<{
      title?: string;
      description?: string;
      volumeNumber?: number;
      pages?: number;
      coverUrl?: string;
      fileUrl?: string;
    }>;
  };
  createdAt: any;
  status: string;
  isPublic: boolean;
}

const ComicDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [comic, setComic] = useState<ComicData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  
  // Edit modal state
  
  // Removed volume selection modal state - now using dedicated pages
  
  const { user } = useAuth();
  const toast = useToast();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const textColor = useColorModeValue('gray.900', 'white');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('purple.500', 'purple.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.800');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('gray.800', 'gray.900');
  const publisherBg = useColorModeValue('gray.50', 'gray.700');
  const publisherBorderColor = useColorModeValue('gray.200', 'gray.600');
  const publisherLabelColor = useColorModeValue('gray.500', 'gray.400');
  const publisherTextColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    const fetchComicData = async () => {
      if (!id) {
        setError('Comic ID is missing');
        setIsLoading(false);
        return;
      }
      
      try {
        const comicDoc = await getDoc(doc(db, 'content', id));
        
        if (comicDoc.exists()) {
          const comicData = { id: comicDoc.id, ...comicDoc.data() } as ComicData;
          
          // Check if it's actually a comic
          if (comicData.category !== 'comic') {
            setError('Content not found');
            return;
          }
          
          setComic(comicData);
        } else {
          setError('Comic not found');
        }
      } catch (error) {
        console.error('Error fetching comic data:', error);
        setError('Failed to load comic data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComicData();
  }, [id]);

  // Check if comic is in library on component mount
  useEffect(() => {
    const checkLibraryStatus = async () => {
      if (user && id) {
        try {
          const inLibrary = await isBookInLibrary(user.uid, id);
          setIsInLibrary(inLibrary);
        } catch (error) {
          console.error('Error checking library status:', error);
        }
      }
    };
    checkLibraryStatus();
  }, [user, id]);

  const handleLibraryToggle = async () => {
    if (!user || !comic) {
      toast({
        title: 'Please sign in',
        description: 'You need to be signed in to add comics to your library',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoadingLibrary(true);
    try {
      if (isInLibrary) {
        await removeBookFromLibrary(user.uid, comic.id);
        setIsInLibrary(false);
        toast({
          title: 'Removed from Library',
          description: `${comic.title} has been removed from your library`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await addBookToLibrary(user.uid, comic.id, {
          title: comic.title,
          author: comic.author,
          cover: comic.thumbnail || comic.assets?.coverFile || ''
        });
        setIsInLibrary(true);
        toast({
          title: 'Added to Library',
          description: `${comic.title} has been added to your library`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error toggling library status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update library status. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  // Helper function to get volume file URL from assets
  const getBookFileUrl = (assets: any) => {
    if (!assets) return '';
    // Prefer volumes array
    if (Array.isArray(assets.volumes) && assets.volumes.length > 0) {
      const firstWithFile = assets.volumes.find((v: any) => v?.fileUrl);
      if (firstWithFile?.fileUrl) return firstWithFile.fileUrl;
    }
    // Fallback: direct bookFile
    if (assets.bookFile) return assets.bookFile;
    // Fallback: any file-like key
    const assetKeys = Object.keys(assets);
    const bookFileKey = assetKeys.find(key => {
      const value = assets[key];
      return typeof value === 'string' && (value.toLowerCase().includes('.pdf') || value.toLowerCase().includes('.epub') || value.toLowerCase().includes('.mobi'));
    });
    if (bookFileKey) return assets[bookFileKey];
    return '';
  };

  const handleReadNow = () => {
    if (!comic) return;
    
    // Check if comic has volumes
    if (comic.assets?.volumes && Array.isArray(comic.assets.volumes) && comic.assets.volumes.length > 0) {
      // Navigate to volume selection page
      navigate(`/comic/${comic.id}/volumes`);
    } else {
      // Fallback to direct reading if no volumes
      const comicFileUrl = getBookFileUrl(comic.assets);
      if (!comicFileUrl) {
        toast({
          title: 'Comic not available',
          description: 'The comic file is not available for reading.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setIsReaderOpen(true);
    }
  };

  // Removed handleVolumeSelect - now handled by ComicVolumes page

  const handleDeleteComic = () => {
    if (!user || !comic) return;
    
    if (user.uid !== comic.uploaderId) {
      toast({
        title: 'Permission Denied',
        description: 'You can only delete your own comics.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onDeleteOpen();
  };

  const confirmDeleteComic = async () => {
    if (!user || !comic) return;

    setIsDeleting(true);
    setDeleteProgress(0);

    toast({
      title: 'Deleting Comic...',
      description: 'Removing comic and all associated data. This may take a moment.',
      status: 'info',
      duration: 10000,
      isClosable: true,
    });

    try {
      const progressInterval = setInterval(() => {
        setDeleteProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      const result = await deleteContent(comic.id, user.uid);
      
      clearInterval(progressInterval);
      setDeleteProgress(100);
      
      if (result.success) {
        toast({
          title: 'Comic Deleted Successfully',
          description: `Deleted ${result.deletedItems.files} files and all associated data.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setTimeout(() => navigate('/creator-dashboard'), 1000);
      } else {
        toast({
          title: 'Delete Failed',
          description: result.message || 'Failed to delete comic. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Error deleting comic:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
      onDeleteClose();
    }
  };


  const handleShare = async () => {
    if (!comic) return;
    
    const shareData = {
      title: comic.title,
      text: `Check out this comic: ${comic.title}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to copying URL
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: 'Link copied!',
          description: 'Comic link has been copied to clipboard.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // User cancelled the share dialog
        return;
      }
      console.error('Error sharing comic:', error);
      toast({
        title: 'Error sharing',
        description: 'Failed to share comic. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="100vh">
        <VStack>
          <Spinner size="xl" color="purple.500" />
          <Text color="white" mt={4}>Loading comic...</Text>
        </VStack>
      </Center>
    );
  }

  if (error || !comic) {
    return (
      <Center h="100vh">
        <VStack>
          <Text fontSize="xl" color="white">Comic not found</Text>
          <Text color="gray.400" mb={4}>The comic you're looking for doesn't exist or has been removed.</Text>
          <Button colorScheme="purple" onClick={() => navigate('/comics')}>
            Browse Comics
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" className="page-content">
      {/* Header */}
      <HStack 
        p={{ base: 3, md: 4 }} 
        bg={headerBg} 
        color="white" 
        justify="space-between"
        position="sticky"
        top="0"
        zIndex="10"
      >
        <HStack>
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            colorScheme="white"
            onClick={() => navigate(-1)}
            size={{ base: 'sm', md: 'md' }}
          />
          <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold">Comic Details</Text>
        </HStack>
        <HStack>
          <IconButton
            aria-label="Share comic"
            icon={<IconWrapper icon={FiShare} size={20} />}
            variant="ghost"
            colorScheme="white"
            onClick={handleShare}
            size={{ base: 'sm', md: 'md' }}
          />
        </HStack>
      </HStack>

      {/* Main Content */}
      <VStack align="stretch" p={{ base: 4, md: 8 }} maxW="1200px" mx="auto">
        {/* Comic Header */}
        <VStack 
          align={{ base: 'center', md: 'start' }} 
          gap={{ base: 6, md: 8 }} 
          direction={{ base: 'column', md: 'row' }}
          w="100%"
        >
          {/* Cover Image */}
          <Box flexShrink={0} alignSelf={{ base: 'center', md: 'flex-start' }}>
            <CachedImage
              src={comic.thumbnail || comic.assets?.coverFile || ''}
              alt={comic.title}
              w={{ base: "200px", sm: "250px", md: "300px" }}
              h={{ base: "280px", sm: "350px", md: "420px" }}
              objectFit="cover"
              borderRadius="lg"
              fallbackSrc="https://via.placeholder.com/300x420?text=Comic+Cover"
            />
          </Box>

          {/* Comic Info */}
          <VStack align={{ base: 'center', md: 'start' }} gap={4} flex="1" w="100%">
            <VStack align={{ base: 'center', md: 'start' }} gap={2} textAlign={{ base: 'center', md: 'left' }}>
              <Text fontSize={{ base: "xl", sm: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} noOfLines={3}>
                {comic.title}
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color={descriptionColor}>by {comic.author}</Text>
            </VStack>

            {/* Rating and Stats */}
            <HStack gap={{ base: 4, md: 6 }} wrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
              <HStack>
                <IconWrapper icon={FiStar} size={16} color="yellow.400" />
                <Text color={textColor} fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }}>{comic.rating.toFixed(1)}</Text>
              </HStack>
              <HStack>
                <IconWrapper icon={FiCalendar} size={16} color={descriptionColor} />
                <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.year}</Text>
              </HStack>
              <HStack>
                <IconWrapper icon={FiGlobe} size={16} color={descriptionColor} />
                <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.language}</Text>
              </HStack>
            </HStack>

            {/* Genres */}
            <HStack gap={2} wrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
              {comic.genres.map((genre, index) => (
                <Badge 
                  key={index} 
                  colorScheme="purple" 
                  variant="solid" 
                  px={{ base: 2, md: 3 }} 
                  py={1}
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  {genre}
                </Badge>
              ))}
            </HStack>

            {/* Action Buttons */}
            <VStack gap={3} w="100%" align={{ base: 'stretch', md: 'flex-start' }}>
              {/* Mobile: Stacked buttons */}
              <VStack gap={3} w="100%" display={{ base: 'flex', md: 'none' }}>
                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={handleReadNow}
                  leftIcon={<IconWrapper icon={FiBook} size={20} />}
                  w="100%"
                >
                  Read Now
                </Button>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  size="lg"
                  onClick={handleLibraryToggle}
                  isLoading={isLoadingLibrary}
                  leftIcon={<IconWrapper icon={FiHeart} size={20} />}
                  w="100%"
                >
                  {isInLibrary ? 'Remove from Library' : 'Add to Library'}
                </Button>
              </VStack>
              
              {/* Desktop: Side by side buttons */}
              <HStack gap={3} w="100%" justify="flex-start" display={{ base: 'none', md: 'flex' }}>
                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={handleReadNow}
                  leftIcon={<IconWrapper icon={FiBook} size={20} />}
                >
                  Read Now
                </Button>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  size="lg"
                  onClick={handleLibraryToggle}
                  isLoading={isLoadingLibrary}
                  leftIcon={<IconWrapper icon={FiHeart} size={20} />}
                >
                  {isInLibrary ? 'Remove from Library' : 'Add to Library'}
                </Button>
              </HStack>
            </VStack>

            {/* Comic Owner Actions */}
            {user && comic && user.uid === comic.uploaderId && (
              <VStack gap={4} mt={6} w="100%">
                <Text fontSize="sm" color="gray.500" fontWeight="medium" textAlign={{ base: 'center', md: 'left' }}>
                  Comic Management
                </Text>
                
                {/* Mobile: Stacked buttons */}
                <VStack gap={3} w="100%" display={{ base: 'flex', md: 'none' }}>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="lg"
                    w="100%"
                    onClick={() => navigate(`/comic/${id}/edit`)}
                  >
                    Edit Comic
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    size="lg"
                    w="100%"
                    onClick={handleDeleteComic}
                    isLoading={isDeleting}
                    loadingText="Deleting..."
                    isDisabled={isDeleting}
                  >
                    Delete Forever
                  </Button>
                </VStack>
                
                {/* Desktop: Side by side buttons */}
                <HStack gap={3} w="100%" justify="flex-start" display={{ base: 'none', md: 'flex' }}>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="md"
                    onClick={() => navigate(`/comic/${id}/edit`)}
                  >
                    Edit Comic
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    size="md"
                    onClick={handleDeleteComic}
                    isLoading={isDeleting}
                    loadingText="Deleting..."
                    isDisabled={isDeleting}
                  >
                    Delete Forever
                  </Button>
                </HStack>
                <Text fontSize="xs" color="gray.400" textAlign="center">
                  Delete: Permanently remove all data and files
                </Text>
                {isDeleting && (
                  <VStack gap={2} w="100%" mt={3}>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      Deleting comic and files... {Math.round(deleteProgress)}%
                    </Text>
                    <Progress 
                      value={deleteProgress} 
                      size="sm" 
                      colorScheme="red" 
                      w="100%" 
                      borderRadius="full"
                    />
                  </VStack>
                )}
              </VStack>
            )}

            {/* Publisher Info */}
            <VStack 
              gap={4} 
              p={4} 
              bg={publisherBg} 
              borderRadius="lg" 
              w="100%" 
              border="1px" 
              borderColor={publisherBorderColor}
            >
              {/* Mobile: Stacked layout */}
              <VStack gap={3} w="100%" display={{ base: 'flex', md: 'none' }}>
                <HStack gap={3} w="100%">
                  <Avatar size="md" src={comic.uploaderAvatar} name={comic.uploader} />
                  <VStack align="start" gap={0} flex="1">
                    <Text fontSize="sm" color={publisherLabelColor} fontWeight="medium">Published by</Text>
                    <Text fontWeight="semibold" color={publisherTextColor} fontSize="md">{comic.uploader}</Text>
                  </VStack>
                </HStack>
                <Button
                  colorScheme="purple"
                  variant="outline"
                  size="md"
                  w="100%"
                  onClick={() => {
                    if (comic.uploaderId) {
                      navigate(`/profile/${comic.uploaderId}`);
                    } else {
                      toast({
                        title: 'Profile not available',
                        description: 'This creator\'s profile is not available.',
                        status: 'warning',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  View Profile
                </Button>
              </VStack>
              
              {/* Desktop: Side by side layout */}
              <HStack gap={3} w="100%" justify="space-between" display={{ base: 'none', md: 'flex' }}>
                <HStack gap={3}>
                  <Avatar size="sm" src={comic.uploaderAvatar} name={comic.uploader} />
                  <VStack align="start" gap={0}>
                    <Text fontSize="sm" color={publisherLabelColor} fontWeight="medium">Published by</Text>
                    <Text fontWeight="semibold" color={publisherTextColor} fontSize="md">{comic.uploader}</Text>
                  </VStack>
                </HStack>
                <Button
                  colorScheme="purple"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    if (comic.uploaderId) {
                      navigate(`/profile/${comic.uploaderId}`);
                    } else {
                      toast({
                        title: 'Profile not available',
                        description: 'This creator\'s profile is not available.',
                        status: 'warning',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  View Profile
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </VStack>

        {/* Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple" w="100%">
          <TabList>
            <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>Description</Tab>
            <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>Details</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} py={4}>
              <VStack align="start" gap={4}>
                <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor}>About this Comic</Text>
                <Text color={textColor} lineHeight="1.6" fontSize={{ base: 'sm', md: 'md' }} textAlign={{ base: 'left', md: 'left' }}>
                  {comic.description}
                </Text>
                {comic.tags && comic.tags.length > 0 && (
                  <VStack align="start" gap={2} w="100%">
                    <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium" color={textColor}>Tags</Text>
                    <HStack gap={2} wrap="wrap" justify={{ base: 'flex-start', md: 'flex-start' }}>
                      {comic.tags.map((tag, index) => (
                        <Badge 
                          key={index} 
                          variant="outline" 
                          colorScheme="purple"
                          fontSize={{ base: 'xs', md: 'sm' }}
                        >
                          #{tag}
                        </Badge>
                      ))}
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </TabPanel>

            <TabPanel px={0} py={4}>
              <VStack align="start" gap={4}>
                <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor}>Comic Details</Text>
                <VStack align="start" gap={4} w="100%">
                  <VStack align="start" gap={2} w="100%">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Author</Text>
                    <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.author}</Text>
                  </VStack>
                  <VStack align="start" gap={2} w="100%">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Year</Text>
                    <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.year}</Text>
                  </VStack>
                  <VStack align="start" gap={2} w="100%">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Language</Text>
                    <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.language}</Text>
                  </VStack>
                  <VStack align="start" gap={2} w="100%">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Genre</Text>
                    <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.genres.join(', ')}</Text>
                  </VStack>
                  <VStack align="start" gap={2} w="100%">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Views</Text>
                    <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{comic.views.toLocaleString()}</Text>
                  </VStack>
                </VStack>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Comic Reader Modal - only for comics without volumes */}
      {isReaderOpen && comic && (
        <BookReader
          bookId={comic.id}
          bookTitle={comic.title}
          bookFileUrl={getBookFileUrl(comic.assets)}
          onClose={() => setIsReaderOpen(false)}
        />
      )}

      {/* Comments Section */}
      <Box maxW="1200px" mx="auto" px={isMobile ? 4 : 8} py={8} pb={{ base: 20, md: 8 }}>
        <CommentSection contentId={comic.id} />
      </Box>



      {/* Volume selection now handled by dedicated ComicVolumes page */}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Comic Permanently
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "{comic?.title}"? This action cannot be undone and will permanently remove:
              <br />
              <br />
              • All comic files and media
              <br />
              • All comments and interactions
              <br />
              • All analytics and view data
              <br />
              • All associated metadata
              <br />
              <br />
              <Text fontWeight="bold" color="red.500">
                This action is irreversible!
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteComic} ml={3} isLoading={isDeleting}>
                Delete Forever
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ComicDetail;
